import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { timeOfDay } from "../../shared/time";
import type { KidSummary } from "../../shared/types";
import { kidStyle } from "../kid-style";
import { chartFloor } from "./ScoreChart";
import { KidMarker, MarkerShape } from "./KidMarker";

/** One kid's day: the score it opened on, then the score after each tap, oldest first. */
export interface DaySeries {
  kid: Pick<KidSummary, "id" | "name" | "colour" | "goal">;
  shape: number;
  opening: number;
  taps: { at: number; score: number }[];
}

const HEIGHT = 240;
const PAD = { top: 12, right: 14, bottom: 26, left: 34 };
const HOUR = 60 * 60 * 1000;

/**
 * Today on one graph: each chosen kid's score from midnight to now as a step line, since a score
 * only moves when a tap lands, with a dot at each tap, the goal dashed behind and the kid's
 * shape at now. Same look as the longer ScoreChart, drawn at the container's real width.
 */
export function DayChart({ series, start, now }: { series: DaySeries[]; start: number; now: number }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setWidth(Math.floor(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (series.every((s) => s.taps.length === 0)) {
    return (
      <div className="chart" ref={wrap}>
        <p className="hint chart__empty">No taps yet today. The line starts with the first one.</p>
      </div>
    );
  }

  // At least an hour wide, so a graph opened just after midnight is not squashed to nothing.
  const end = Math.max(now, start + HOUR);
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const goals = [...new Set(series.map((s) => s.kid.goal))];
  const floor = chartFloor([...series.flatMap((s) => [s.opening, ...s.taps.map((t) => t.score)]), ...goals]);
  const zoomed = floor > 0;
  const yTicks = zoomed
    ? Array.from({ length: (100 - floor) / 10 + 1 }, (_, k) => floor + k * 10)
    : [0, 25, 50, 75, 100];
  const x = (t: number) => Math.round((PAD.left + ((t - start) / (end - start)) * plotW) * 10) / 10;
  const y = (v: number) => Math.round((PAD.top + (1 - (v - floor) / (100 - floor)) * plotH) * 10) / 10;
  // Every six hours from midnight, up to now.
  const ticks = [0, 6, 12, 18].map((h) => start + h * HOUR).filter((t) => t <= end);

  // One label per goal, in the kid's colour when goals differ; one too close to the last is left out.
  let lastLabel = -Infinity;
  const goalLabels = series
    .map((s) => ({ goal: s.kid.goal, colour: goals.length === 1 ? null : s.kid.colour }))
    .sort((a, b) => b.goal - a.goal)
    .filter((g, j, all) => all.findIndex((o) => o.goal === g.goal) === j)
    .filter((g) => {
      if (y(g.goal) - lastLabel < 16) return false;
      lastLabel = y(g.goal);
      return true;
    });

  const lines = series.map((s) => {
    const steps = s.taps.map((t) => `H${x(t.at)}V${y(t.score)}`).join("");
    return { ...s, path: `M${x(start)} ${y(s.opening)}${steps}H${x(now)}`, score: s.taps.at(-1)?.score ?? s.opening };
  });
  const label = `Score today${zoomed ? `, zoomed to ${floor} to 100` : ""}: ${lines
    .map((l) => `${l.kid.name} ${l.score} now, ${l.taps.length} ${l.taps.length === 1 ? "tap" : "taps"}, goal ${l.kid.goal}`)
    .join("; ")}.`;

  return (
    <div className="chart" ref={wrap}>
      <ul className="chart__legend" aria-hidden="true">
        {series.map((s) => (
          <li key={s.kid.id} className="chart__key" style={kidStyle(s.kid.colour)}>
            <KidMarker shape={s.shape} />
            {s.kid.name}
          </li>
        ))}
        {zoomed && <li className="chart__zoom">{`${floor}–100 shown`}</li>}
      </ul>
      <div className="chart__readout" aria-hidden="true">
        <span className="chart__pill chart__pill--end">
          <span>{`Now ${timeOfDay(now)}`}</span>
          {lines.map((l) => (
            <span key={l.kid.id} className="chart__reading" style={kidStyle(l.kid.colour)}>
              <KidMarker shape={l.shape} />
              {l.score}
            </span>
          ))}
        </span>
      </div>
      {width > 0 && (
        <svg className="chart__svg" width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={label}>
          {yTicks.map((v) => (
            <g key={v}>
              <line className="chart__grid" x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
              <text className="chart__axis" x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle">
                {v}
              </text>
            </g>
          ))}
          {zoomed && (
            <path
              className="chart__break"
              d={`M${PAD.left - 4} ${y(floor) + 9}l8 -5M${PAD.left - 4} ${y(floor) + 14}l8 -5`}
            />
          )}
          {ticks.map((t) => (
            <text
              key={t}
              className="chart__axis"
              x={x(t)}
              y={HEIGHT - 6}
              textAnchor={t === start ? "start" : x(t) > width - PAD.right - 18 ? "end" : "middle"}
            >
              {timeOfDay(t)}
            </text>
          ))}
          <g className="chart__reveal">
            {series.map((s) => (
              <line
                key={s.kid.id}
                className={goals.length === 1 ? "chart__goal" : "chart__goal chart__goal--kid"}
                style={kidStyle(s.kid.colour)}
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(s.kid.goal)}
                y2={y(s.kid.goal)}
              />
            ))}
            {goalLabels.map((g) => (
              <text
                key={g.goal}
                className={g.colour ? "chart__goal-label chart__goal-label--kid" : "chart__goal-label"}
                style={g.colour ? kidStyle(g.colour) : undefined}
                x={PAD.left + 4}
                y={y(g.goal) - 7}
              >
                {`goal ${g.goal}`}
              </text>
            ))}
          </g>
          {lines.map((l, k) => (
            <g
              key={l.kid.id}
              className="chart__reveal"
              style={{ ...kidStyle(l.kid.colour), animationDelay: `${k * 90}ms` } as CSSProperties}
            >
              <path className="chart__line" d={l.path} />
              {l.taps.map((t, i) => (
                <circle key={i} className="chart__star-dot" cx={x(t.at)} cy={y(t.score)} r={3} />
              ))}
              <MarkerShape shape={l.shape} className="chart__today" cx={x(now)} cy={y(l.score)} r={7} />
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}
